import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  forwardRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  Briefcase,
  Palmtree,
  MapPin,
  IdCard,
  SlidersHorizontal,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Modal2,
  Modal2Footer,
  Modal2Button,
  ConfirmationModal,
  Button,
  SimpleTooltip,
  OverflowTooltip,
  PageHeader,
} from "../common";
import Select from "../common/Select";
import Input from "../common/Input";
import RadioButton from "../common/RadioButton";
import { useMasterConfig } from "../../store/hooks/useMasterConfig";
import { useAppDispatch } from "../../store/hooks";
import { masterConfigApi } from "../../store/apis/masterConfig.api";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { extractApiError } from "../../utils/apiErrorUtils";
import {
  buildMasterConfigFilterCode,
  buildMasterConfigGroups,
  type MasterConfigCategoryOption,
  type MasterConfigGroup,
  type MasterConfigItem,
} from "../../utils/masterConfigUtils";
import { ConfigsPanelSkeleton } from "./Skeleton";

/** Imperative entry points for hosts (e.g. Settings hub header actions). */
export interface FilterManagementHandle {
  openAdd: (categoryCode?: string) => void;
}

export interface FilterManagementProps {
  /** Render bare on the page (host provides header + surface, e.g. Settings hub). */
  bare?: boolean;
}

type ModalState =
  | { mode: "add"; categoryCode: string }
  | { mode: "edit"; item: MasterConfigItem }
  | null;

/**
 * Group icons mirror Figma (CrewKnit HRMS dc.html masterGroups) and reuse the
 * app's established tile palette (SettingsPage iconClass tokens). Unknown
 * backend categories fall back to the Master Config identity
 * (SlidersHorizontal + primary tile) so nothing looks invented.
 */
const TILE_TOKENS = [
  "bg-primary-100 text-primary-700",
  "bg-secondary-100 text-secondary-600",
  "bg-success-light text-success-dark",
  "bg-warning-light text-warning-dark",
  "bg-error-light text-error-dark",
];

const iconForCategory = (code: string): React.ReactNode => {
  const c = code.toLowerCase();
  const cls = "h-[22px] w-[22px]";
  if (c.includes("department")) return <Building2 className={cls} aria-hidden="true" />;
  if (c.includes("designation")) return <Briefcase className={cls} aria-hidden="true" />;
  if (c.includes("leave")) return <Palmtree className={cls} aria-hidden="true" />;
  if (c.includes("location")) return <MapPin className={cls} aria-hidden="true" />;
  if (c.includes("employment")) return <IdCard className={cls} aria-hidden="true" />;
  return <SlidersHorizontal className={cls} aria-hidden="true" />;
};

const tileForCategory = (code: string, index: number): string => {
  const c = code.toLowerCase();
  if (c.includes("department")) return TILE_TOKENS[0];
  if (c.includes("designation")) return TILE_TOKENS[1];
  if (c.includes("leave")) return TILE_TOKENS[2];
  if (c.includes("location")) return TILE_TOKENS[3];
  if (c.includes("employment")) return TILE_TOKENS[4];
  return TILE_TOKENS[index % TILE_TOKENS.length];
};

/** Master Configuration — Figma group-card + chip grid. Data contracts unchanged. */
const FilterManagement = forwardRef<FilterManagementHandle, FilterManagementProps>(
  function FilterManagement({ bare = false }, ref) {
  const {
    categoryCodes,
    groupedConfigs,
    createMasterConfig,
    updateMasterConfig,
    deleteMasterConfig,
    isLoading,
  } = useMasterConfig();

  const canManage = useHasPermission([PERMISSIONS.MASTER_CONFIG_MANAGE]);

  // Embedded under /admin/settings the hub renders header + card container.
  const { pathname } = useLocation();
  const showHeader = !pathname.startsWith("/admin/settings");
  const embedded = !showHeader;

  const [modal, setModal] = useState<ModalState>(null);
  const [modalValue, setModalValue] = useState("");
  const [modalActive, setModalActive] = useState(true);
  const [modalGroup, setModalGroup] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<MasterConfigItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useAppDispatch();

  // getConfigsGroupedByCategory declares no providesTags, so tag
  // invalidation never refetches it — refresh explicitly after mutations
  // (read-only dispatch, no API/slice/backend change).
  const refreshGroups = useCallback(() => {
    dispatch(
      masterConfigApi.endpoints.getConfigsGroupedByCategory.initiate(
        undefined,
        { subscribe: false, forceRefetch: true },
      ),
    );
    dispatch(
      masterConfigApi.endpoints.getCategoryCodes.initiate(undefined, {
        subscribe: false,
        forceRefetch: true,
      }),
    );
  }, [dispatch]);

  const categories: MasterConfigCategoryOption[] = useMemo(() => {
    const data = (categoryCodes as { data?: unknown } | undefined)?.data;
    return Array.isArray(data) ? (data as MasterConfigCategoryOption[]) : [];
  }, [categoryCodes]);

  const groups: MasterConfigGroup[] = useMemo(
    () => buildMasterConfigGroups(categories, groupedConfigs),
    [categories, groupedConfigs],
  );

  const openAdd = useCallback(
    (categoryCode?: string) => {
      if (!canManage) return;
      const fallback = categories[0]?.categoryCode ?? "";
      setModalGroup(categoryCode ?? fallback);
      setModalValue("");
      setModalActive(true);
      setModal({ mode: "add", categoryCode: categoryCode ?? fallback });
    },
    [canManage, categories],
  );

  useImperativeHandle(
    ref,
    () => ({
      openAdd: (categoryCode?: string) => openAdd(categoryCode),
    }),
    [openAdd],
  );

  const openEdit = useCallback(
    (item: MasterConfigItem) => {
      if (!canManage) return;
      if (!item.id) {
        toast.error("This value has no identifier and cannot be edited.");
        return;
      }
      setModalValue(item.displayName);
      setModalActive(item.isActive);
      setModal({ mode: "edit", item });
    },
    [canManage],
  );

  const closeModal = useCallback(() => {
    if (isSaving) return;
    setModal(null);
  }, [isSaving]);

  const handleSave = useCallback(async () => {
    if (!modal || !canManage || isSaving) return;
    const displayName = modalValue.trim();
    if (!displayName) {
      toast.error("Enter a value first");
      return;
    }
    setIsSaving(true);
    try {
      if (modal.mode === "add") {
        const categoryCode = modalGroup || modal.categoryCode;
        const category = categories.find(
          (c) => c.categoryCode === categoryCode,
        );
        await createMasterConfig({
          categoryCode,
          categoryName: category?.categoryName || "",
          displayName,
          filterCode: buildMasterConfigFilterCode("", displayName),
          isActive: modalActive,
        }).unwrap();
        toast.success("Value added");
        refreshGroups();
      } else {
        const { item } = modal;
        if (!item.id) {
          toast.error("This value has no identifier and cannot be saved.");
          return;
        }
        await updateMasterConfig({
          id: item.id,
          data: {
            categoryCode: item.categoryCode,
            categoryName: item.categoryName,
            displayName,
            filterCode: buildMasterConfigFilterCode(item.filterCode, displayName),
            isActive: modalActive,
          },
        }).unwrap();
        toast.success("Value updated");
        refreshGroups();
      }
      setModal(null);
    } catch (error: unknown) {
      toast.error(extractApiError(error) || "Failed to save value");
    } finally {
      setIsSaving(false);
    }
  }, [
    modal,
    modalValue,
    modalActive,
    modalGroup,
    categories,
    canManage,
    isSaving,
    createMasterConfig,
    updateMasterConfig,
    refreshGroups,
  ]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete?.id) {
      toast.error("This value has no identifier and cannot be deleted.");
      setPendingDelete(null);
      return;
    }
    setIsDeleting(true);
    try {
      await deleteMasterConfig(pendingDelete.id).unwrap();
      toast.success("Value deleted");
      refreshGroups();
      setPendingDelete(null);
      setModal(null);
    } catch (error: unknown) {
      toast.error(extractApiError(error) || "Failed to delete value");
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete, deleteMasterConfig, refreshGroups]);

  const modalGroupName = useMemo(() => {
    if (!modal) return "";
    if (modal.mode === "edit") return modal.item.categoryName;
    return (
      categories.find((c) => c.categoryCode === modalGroup)?.categoryName ?? ""
    );
  }, [modal, modalGroup, categories]);

  const renderChip = useCallback(
    (item: MasterConfigItem) => {
      return (
        <span
          key={item.id ?? `${item.categoryCode}-${item.filterCode}`}
          className={
            item.isActive
              ? "inline-flex max-w-[240px] items-center gap-1 rounded-full bg-layout-muted py-1 pl-3 pr-1.5 text-body-sm font-semibold text-strong"
              : "inline-flex max-w-[240px] items-center gap-1 rounded-full border border-dashed border-border-primary bg-surface py-1 pl-3 pr-1.5 text-body-sm font-semibold text-text-tertiary"
          }
        >
          <OverflowTooltip className="block min-w-0 flex-1 truncate">
            {item.displayName}
          </OverflowTooltip>
          {canManage && (
            <>
              <SimpleTooltip label={`Edit ${item.displayName}`} side="top">
                <button
                  type="button"
                  aria-label={`Edit ${item.displayName}`}
                  onClick={() => openEdit(item)}
                  className="grid h-6 w-6 flex-none place-items-center rounded-full text-text-tertiary transition-colors hover:bg-surface hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Pencil className="h-3 w-3" aria-hidden="true" />
                </button>
              </SimpleTooltip>
              <SimpleTooltip label={`Delete ${item.displayName}`} side="top">
                <button
                  type="button"
                  aria-label={`Delete ${item.displayName}`}
                  onClick={() => setPendingDelete(item)}
                  className="grid h-6 w-6 flex-none place-items-center rounded-full transition-colors hover:bg-error-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Trash2 className="h-3.5 w-3.5 text-error-dark" aria-hidden="true" />
                </button>
              </SimpleTooltip>
            </>
          )}
        </span>
      );
    },
    [canManage, openEdit],
  );

  const body = (() => {
    if (isLoading) return <ConfigsPanelSkeleton />;
    if (groups.length === 0) {
      return (
        <div className="rounded-card border border-border-secondary bg-surface px-6 py-14 text-center shadow-soft">
          <h3 className="mb-1 text-heading-sm text-text-primary">
            No master configs found
          </h3>
          <p className="mx-auto mb-5 max-w-md text-body-sm text-text-secondary">
            Categories created for dropdowns and reference data will appear
            here as group cards.
          </p>
          {canManage && (
            <Button
              appearance="primary"
              size="middle"
              onClick={() => openAdd()}
              icon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              Add Value
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2">
        {groups.map((group, gi) => (
          <section
            key={group.categoryCode}
            aria-label={group.categoryName}
            className="rounded-card border border-border-secondary bg-surface p-5 shadow-soft sm:p-6"
          >
            <div className="mb-4 flex items-center gap-3">
              <span
                className={`grid h-[42px] w-[42px] flex-none place-items-center rounded-lg ${tileForCategory(group.categoryCode, gi)}`}
              >
                {iconForCategory(group.categoryCode)}
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-heading-sm text-text-primary">
                  {group.categoryName}
                </h2>
                <p className="text-body-xs text-text-tertiary">
                  {group.activeCount} active · {group.total} total
                </p>
              </div>
              {canManage && (
                <SimpleTooltip label={`Add to ${group.categoryName}`} side="top">
                  <button
                    type="button"
                    aria-label={`Add to ${group.categoryName}`}
                    onClick={() => openAdd(group.categoryCode)}
                    className="grid h-8 w-8 flex-none place-items-center rounded-button border border-border-secondary bg-surface text-text-secondary transition-colors hover:bg-layout-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </button>
                </SimpleTooltip>
              )}
            </div>
            {group.items.length > 0 ? (
              <div
                role="region"
                aria-label={`${group.categoryName} values — scroll for more`}
                tabIndex={0}
                className="flex h-[72px] flex-wrap content-start gap-2 overflow-y-auto pr-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [scrollbar-color:var(--color-border-input)_transparent] [scrollbar-width:thin]"
              >
                {group.items.map(renderChip)}
              </div>
            ) : (
              <p className="text-body-sm italic text-text-tertiary">
                No values yet
                {canManage ? " — use + to add the first value." : "."}
              </p>
            )}
          </section>
        ))}
      </div>
    );
  })();

  return (
    <div className={embedded ? "w-full" : "w-full animate-fade-in"}>
      {showHeader && (
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <PageHeader
            className="mb-0 flex-1"
            title="Master Configuration"
            description="Manage dropdowns and reference data"
            breadcrumbs={[
              { label: "Settings", path: "/admin/settings" },
              { label: "Master Config" },
            ]}
          />
          {canManage && (
            <Button
              appearance="primary"
              size="middle"
              onClick={() => openAdd()}
              icon={<Plus className="h-4 w-4" aria-hidden="true" />}
              className="flex-none"
            >
              Add Value
            </Button>
          )}
        </div>
      )}

      {!canManage && (
        <div className="mb-5 rounded-card border border-border-secondary bg-surface px-5 py-4 shadow-soft">
          <p className="text-body-sm text-text-secondary">
            You have view-only access to master configs. Contact an admin to
            create or edit values.
          </p>
        </div>
      )}

      {embedded && canManage && !bare && (
        <div className="mb-4 flex items-center justify-end">
          <Button
            appearance="primary"
            size="middle"
            onClick={() => openAdd()}
            icon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Add Value
          </Button>
        </div>
      )}

      {body}

      <Modal2
        isOpen={modal !== null}
        onClose={closeModal}
        title={modal?.mode === "edit" ? "Edit Value" : "Add Value"}
        subtitle={modal ? `Value for ${modalGroupName}` : undefined}
        size="sm"
        footer={
          <Modal2Footer justify="end">
            <Modal2Button
              variant="secondary"
              onClick={closeModal}
              disabled={isSaving}
            >
              Cancel
            </Modal2Button>
            <Modal2Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !modalValue.trim()}
            >
              {modal?.mode === "edit" ? "Save Changes" : "Add Value"}
            </Modal2Button>
          </Modal2Footer>
        }
      >
        <div className="space-y-4">
          {modal?.mode === "add" && (
            <Select
              label="Group"
              required
              value={modalGroup}
              onChange={(v) => {
                const code = String(v);
                setModalGroup(code);
                setModal({ mode: "add", categoryCode: code });
              }}
              options={categories.map((c) => ({
                label: c.categoryName,
                value: c.categoryCode,
              }))}
              placeholder="Select group"
              searchable
            />
          )}
          <Input
            label="Value"
            required
            placeholder="Enter value"
            value={modalValue}
            onChange={(v) => setModalValue(String(v))}
            disabled={isSaving}
            onEnter={handleSave}
          />
          <RadioButton
            name="master-config-status"
            label="Status"
            value={modalActive ? "active" : "inactive"}
            onChange={(v) => setModalActive(v === "active")}
            options={[
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ]}
            direction="row"
            disabled={isSaving}
          />
        </div>
      </Modal2>

      <ConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete Value"
        message={`Are you sure you want to delete '${pendingDelete?.displayName}'? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
});

export default FilterManagement;
